import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Venus,
  Cake,
  Phone,
  Globe2,
  Languages,
  MapPin,
  MailOpen,
  CreditCard,
  Loader2,
  User,
  ShieldCheck,
  FileText,
  ArrowLeft,
} from "lucide-react";
import "./profile.css";

const API_URL = "https://sl-back-sigma.vercel.app";

const buildInfoRows = (user) => {
  const fullAddress = [user.Adresse, user.Ville, user.Pays].filter(Boolean).join(", ");
  return [
    { Icon: Venus,      label: "Genre",            value: user.gender },
    { Icon: Cake,       label: "Date de naissance", value: user.DOB ? new Date(user.DOB).toISOString().split("T")[0] : "—"},
    { Icon: Phone,      label: "Téléphone",         value: user.phone_number || user.phone },
    { Icon: Globe2,     label: "Nationalité",       value: user.nationality },
    { Icon: Languages,  label: "Langue maternelle", value: user.MaternalLanguage },
    { Icon: MapPin,     label: "Adresse",           value: fullAddress },
    { Icon: MailOpen,   label: "Code Postal",       value: user.Code_postal },
    { Icon: CreditCard, label: "Numéro ID",         value: user.id_number },
  ];
};

const InfoCard = ({ Icon, label, value, delay }) => (
  <motion.div
    className="info-card"
    initial={{ opacity: 0, y: 14 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3, delay, ease: "easeOut" }}
    whileHover={{ y: -2 }}
  >
    <div className="info-card-icon-wrap">
      <Icon size={17} strokeWidth={1.8} />
    </div>
    <div className="info-card-text">
      <span className="info-card-label">{label}</span>
      <span className="info-card-value">{value || "—"}</span>
    </div>
  </motion.div>
);

const StatPill = ({ count, label }) => (
  <div className="stat-pill">
    <span className="stat-count">{count}</span>
    <span className="stat-label">{label}</span>
  </div>
);

const ProfileUser = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("info");

  // Fetch user by ID
  useEffect(() => {
    const fetchUser = async () => {
      try {
        setLoading(true);
        setError(null);
        const token = localStorage.getItem("token");
        const authHeader = token?.startsWith("Bearer ") ? token : `Bearer ${token}`;
        const resp = await axios.get(`${API_URL}/user/`, {
          headers: { Authorization: authHeader },
        });
        const users = resp.data.users || resp.data;
        const foundUser = Array.isArray(users) ? users.find(u => String(u._id) === String(id)) : null;
        if (foundUser) {
          setUser(foundUser);
        } else {
          setError("Utilisateur introuvable.");
        }
      } catch (err) {
        console.error(err);
        setError("Impossible de charger cet utilisateur.");
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchUser();
  }, [id]);

  // Fetch payments for this user when payment tab is active
  useEffect(() => {
    const fetchPayments = async () => {
      try {
        const token = localStorage.getItem("token");
        const authHeader = token?.startsWith("Bearer ") ? token : `Bearer ${token}`;
        const resp = await axios.get(`${API_URL}/payment/all`, {
          headers: { Authorization: authHeader },
        });
        const allOrders = resp.data.orders || [];
        // Filter payments for this specific user
        const userPayments = allOrders.filter(
          (o) => o.id_user?._id === id || o.id_user === id
        );
        setPayments(userPayments);
      } catch (err) {
        console.error(err);
      }
    };

    if (activeTab === "payment" && id) fetchPayments();
  }, [activeTab, id]);

  if (loading) {
    return (
      <div className="profile-loading">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
        >
          <Loader2 size={32} color="var(--brand)" strokeWidth={1.5} />
        </motion.div>
        <p>Chargement du profil…</p>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="profile-loading">
        <p style={{ color: "var(--danger, #dc3545)" }}>{error || "Utilisateur introuvable."}</p>
        <button className="edit-btn" onClick={() => navigate(-1)} style={{ marginTop: 16 }}>
          <ArrowLeft size={14} strokeWidth={2} /> Retour
        </button>
      </div>
    );
  }

  const initials = `${user?.name?.[0] ?? ""}${user?.last_name?.[0] ?? ""}`.toUpperCase();
  const infoRows = buildInfoRows(user);

  return (
    <>
      <div className="profile-layout">
        <main className="profile-main" style={{ marginLeft: 0 }}>
          <div className="profile-wrapper">

            {/* Back button */}
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
              style={{ marginBottom: 16 }}
            >
              <button className="edit-btn" onClick={() => navigate(-1)}>
                <ArrowLeft size={14} strokeWidth={2} /> Retour
              </button>
            </motion.div>

            {/* ── cover + avatar ── */}
            <motion.div
              className="profile-cover"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <div className="cover-pattern" />

              <div className="cover-bottom">
                <div className="avatar-stack">
                  <motion.div
                    className="avatar-ring"
                    initial={{ scale: 0.7, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.4, delay: 0.1, type: "spring", stiffness: 200 }}
                  >
                    <div className="avatar-initials">{initials}</div>
                    <div className="avatar-verified">
                      <ShieldCheck size={12} strokeWidth={2.5} />
                    </div>
                  </motion.div>

                  <motion.div
                    className="identity-block"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.35, delay: 0.2 }}
                  >
                    <h1 className="profile-name">{user?.name} {user?.last_name}</h1>
                    <p className="profile-email">{user?.email}</p>
                    {user.category && (
                      <span className="profile-badge">{user?.category}</span>
                    )}
                  </motion.div>
                </div>

                <motion.div
                  className="cover-actions"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.25 }}
                >
                  <div className="stats-row">
                    <StatPill count={user.id_number || "—"} label="ID" />
                    <div className="stat-divider" />
                    <StatPill count={user.nationality || "—"} label="Nationalité" />
                    <div className="stat-divider" />
                    <StatPill count={user.MaternalLanguage || "—"} label="Langue" />
                  </div>
                </motion.div>
              </div>
            </motion.div>

            {/* ── tabs ── */}
            <div className="profile-tabs">
              <button
                className={`profile-tab ${activeTab === "info" ? "active" : ""}`}
                onClick={() => setActiveTab("info")}
              >
                <User size={14} strokeWidth={1.8} /> Informations personnelles
                {activeTab === "info" && (
                  <motion.div className="tab-underline" layoutId="tab-underline" />
                )}
              </button>
              <button
                className={`profile-tab ${activeTab === "payment" ? "active" : ""}`}
                onClick={() => setActiveTab("payment")}
              >
                <CreditCard size={14} strokeWidth={1.8} /> Paiements
                {activeTab === "payment" && (
                  <motion.div className="tab-underline" layoutId="tab-underline" />
                )}
              </button>
            </div>

            {/* ── content panel ── */}
            <AnimatePresence mode="wait">
              {activeTab === "info" ? (
                <motion.div
                  key="info"
                  className="profile-panel"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.22 }}
                >
                  <div className="panel-header">
                    <span className="panel-title">Informations personnelles</span>
                  </div>
                  <div className="info-grid">
                    {infoRows.map((row, i) => (
                      <InfoCard key={i} delay={i * 0.04} {...row} />
                    ))}
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="payment"
                  className="profile-panel"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.22 }}
                >
                  <div className="panel-header">
                    <span className="panel-title">Historique des paiements</span>
                  </div>

                  {!payments || payments.length === 0 ? (
                    <div style={{ padding: "40px 20px", textAlign: "center", color: "var(--text-muted)" }}>
                      <FileText size={40} strokeWidth={1} style={{ marginBottom: "16px", opacity: 0.5 }} />
                      <p>Aucun paiement enregistré pour cet utilisateur.</p>
                    </div>
                  ) : (
                    <div style={{ overflowX: "auto" }}>
                      <table className="payments-table">
                        <thead>
                          <tr>
                            <th>Type</th>
                            <th>Examen</th>
                            <th>Date d'examen</th>
                            <th>Montant</th>
                            <th>Statut</th>
                            <th>Payé le</th>
                          </tr>
                        </thead>
                        <tbody>
                          {payments.map((order) => (
                            <tr key={order._id}>
                              <td>
                                <span className={`payment-type-badge ${order.product?.type?.toLowerCase() || ""}`}>
                                  {order.product?.type || "—"}
                                </span>
                              </td>
                              <td>{order.product?.examType || "—"}</td>
                              <td>
                                {order.product?.date
                                  ? new Date(order.product.date).toISOString().split("T")[0]
                                  : "—"}
                              </td>
                              <td className="payment-amount">
                                {order.amount} {order.currency?.toUpperCase() || "CAD"}
                              </td>
                              <td>
                                <span className={`payment-status ${order.paymentStatus}`}>
                                  {order.paymentStatus === "succeeded"
                                    ? "Réussi"
                                    : order.paymentStatus === "pending"
                                      ? "En attente"
                                      : "Échoué"}
                                </span>
                              </td>
                              <td style={{ fontSize: "0.82rem", color: "var(--text-muted)" }}>
                                {order.createdAt ? new Date(order.createdAt).toISOString().split("T")[0] : "—"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

          </div>
        </main>
      </div>
    </>
  );
};

export default ProfileUser;